import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, firstValueFrom, map, of } from 'rxjs';
import {
  FormulaDTO,
  ItemOrdenDTO,
  OrdenCrearDTO,
  OrdenDTO,
  TipoPreparacion,
} from './ordenes.models';
import {
  AccionMasivaDTO,
  ListaDTO,
  Parametros,
} from '../../compartido/utilidades/compartido.models';
import { ErrorFrontend } from 'src/app/compartido/utilidades/error.utils';
import { Orden } from './orden.class';
import { stringAleatorio } from 'src/app/compartido/utilidades/valores.utils';

@Injectable({ providedIn: 'root', deps: [HttpClient] })
export class OrdenesService {
  constructor(private readonly _http: HttpClient) {}

  /**
   * Obtiene las órdenes del servidor.
   * @param {Parametros} params - Los parámetros de búsqueda.
   * @returns {Promise<ListaDTO<OrdenDTO>>} - Las órdenes obtenidos del servidor (promise).
   * @throws {ErrorFrontend} - Si hubo un error al obtener las órdenes.
   * @author Juan Corral
   */
  public obtenerOrdenes$(params: Parametros): Promise<ListaDTO<OrdenDTO>> {
    return firstValueFrom(
      this._http.get<ListaDTO<OrdenDTO>>('ordenes/', { params }).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /**
   * Obtiene información de la orden del servidor.
   * @param {number} id - El id de la orden a obtener.
   * @param {boolean} sinAutenticacion [Opcional] - Si se utiliza el endpoint sin autenticación.
   * @returns {Promise<Orden> } - La respuesta del servidor (promise).
   * @throws {ErrorFrontend} - Si hubo un error al obtener la orden.
   * @author Juan Corral
   */
  public obtenerOrden$(
    id: number,
    sinAutenticacion: boolean = false,
  ): Promise<Orden> {
    const url = sinAutenticacion
      ? `ordenes/${id}/obtener_sin_auth/`
      : 'ordenes/' + id;
    return firstValueFrom(
      this._http.get<OrdenDTO>(url).pipe(
        map((orden: OrdenDTO) => Orden.fromDTO(orden)),
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /**
   * Crea la orden en el servidor.
   * @param {OrdenCrearDTO} orden - La orden a crear.
   * @param {boolean} sinAutenticacion [Opcional] - Si se utiliza el endpoint sin autenticación.
   * @returns {Promise<Orden>} - La orden creada (promise).
   * @throws {ErrorFrontend} - Si hubo un error al crear la orden.
   * @author Juan Corral
   */
  public async crearOrden$(
    orden: OrdenCrearDTO,
    sinAutenticacion: boolean = false,
  ): Promise<Orden> {
    const formulas = orden.formulas;
    orden.formulas = [];
    // Crear la orden
    const url = sinAutenticacion ? 'ordenes/crear_sin_auth/' : 'ordenes/';
    const creada = await firstValueFrom(
      this._http.post<OrdenDTO>(url, orden).pipe(
        map((res: OrdenDTO) => Orden.fromDTO(res)),
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
    // Subir las formulas
    if (formulas && formulas.length > 0) {
      for (let i = 0; i < formulas.length; i++) {
        const datos = new FormData();
        datos.append('orden', creada.id.toString());
        if (!!formulas[i].referencia)
          datos.append('referencia', String(formulas[i].referencia));
        const archivo = formulas[i].archivo;
        if (!!archivo) {
          const extension = archivo.name.split('.').pop();
          datos.append(
            'archivo',
            archivo,
            `${creada.id}_${i}_${stringAleatorio(7)}.${extension}`,
          );
        }
        const url = sinAutenticacion
          ? 'ordenes/formulas/crear_sin_auth/'
          : 'ordenes/formulas/';
        await firstValueFrom(
          this._http.post(url, datos).pipe(
            catchError((error: HttpErrorResponse) => {
              throw new ErrorFrontend(error);
            }),
          ),
        );
      }
    }
    return creada;
  }

  /**
   * Crea las órdenes en el servidor.
   * @param {OrdenCrearDTO[]} ordenes - Las órdenes a crear.
   * @returns {Promise<AccionMasivaDTO<string, object>>} - La respuesta del servidor (promise).
   * @author Juan Corral
   */
  public crearOrdenes$(
    ordenes: OrdenCrearDTO[],
  ): Promise<AccionMasivaDTO<string, object>> {
    return firstValueFrom(
      this._http
        .post<
          AccionMasivaDTO<string, object>
        >('ordenes/creacion_masiva/', ordenes)
        .pipe(
          catchError(() =>
            of({ resultado: [], exitosos: 0, fallidos: 0, total: 0 }),
          ),
        ),
    );
  }

  /**
   * Cancela una orden.
   * @param {number} id - El id de la orden a cancelar.
   * @param {string} motivo - El motivo de la cancelación.
   * @param {string} observacion [Opcional] - La observación de la cancelación.
   * @throws {ErrorFrontend} - Si hubo un error al cancelar la orden.
   * @autor Juan Corral
   */
  public async cancelarOrden$(
    id: number,
    motivo: string,
    observacion?: string,
  ): Promise<void> {
    await firstValueFrom(
      this._http
        .post<void>('ordenes/' + id + '/cancelar/', { motivo, observacion })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            throw new ErrorFrontend(error);
          }),
        ),
    );
  }

  /**
   * Aplaza la preparación de la orden.
   * @param {number} id - El id de la orden a aplazar.
   * @param {string} fecha - La fecha de aplazamiento.
   * @param {string} motivo - El motivo del aplazamiento.
   * @param {string} observacion [Opcional] - La observación del aplazamiento.
   * @returns {Promise<Orden>} - La orden actualizada (promise).
   * @throws {ErrorFrontend} - Si hubo un error al aplazar la orden.
   * @author Juan Corral
   */
  public async aplazarPreparacionOrden$(
    id: number,
    fecha: string,
    motivo: string,
    observacion?: string,
  ): Promise<Orden> {
    const datos = { fecha, motivo, observacion };
    return firstValueFrom(
      this._http
        .post<OrdenDTO>(`ordenes/${id}/aplazar_preparacion/`, datos)
        .pipe(
          map((orden: OrdenDTO) => Orden.fromDTO(orden)),
          catchError((error: HttpErrorResponse) => {
            throw new ErrorFrontend(error);
          }),
        ),
    );
  }

  /**
   * Rechaza la orden en el servidor.
   * @param {number} id - El id de la orden a rechazar.
   * @param {string} motivo - El motivo del rechazo.
   * @param {string} observacion - La observación del rechazo.
   * @returns {Promise<Orden>} - La orden actualizada (promise).
   * @throws {ErrorFrontend} - Si hubo un error al rechazar la orden.
   * @author Juan Corral
   */
  public async rechazarOrden$(
    id: number,
    motivo: string,
    observacion: string,
  ): Promise<Orden> {
    const datos = { motivo, observacion };
    return firstValueFrom(
      this._http.post<OrdenDTO>(`ordenes/${id}/rechazar/`, datos).pipe(
        map((orden: OrdenDTO) => Orden.fromDTO(orden)),
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /**
   * Prepara la orden en el servidor.
   * @param {number} id - El id de la orden a preparar.
   * @param {TipoPreparacion} tipo - Tipo de preparación.
   * @param {string} observacion - La observación de la orden.
   * @param {boolean} sinDomicilio - Si la orden no tendrá domicilio.
   * @param {number} recaudo [Opcional] - El valor del recaudo del domicilio.
   * @param {string} motivoSinDomicilio [Opcional] - El motivo por el cual no tiene domicilio.
   * @returns {Promise<Orden>} - La orden actualizada (promise).
   * @throws {ErrorFrontend} - Si hubo un error al preparar la orden.
   * @author Juan Corral
   */
  public prepararOrden$(
    id: number,
    tipo: TipoPreparacion,
    observacion: string,
    sinDomicilio: boolean,
    recaudo?: number,
    motivoSinDomicilio?: string,
  ): Promise<Orden> {
    const datos = {
      tipo,
      observacion,
      sin_domicilio: sinDomicilio,
      recaudo,
      motivo_sin_domicilio: motivoSinDomicilio,
    };
    return firstValueFrom(
      this._http.patch<OrdenDTO>(`ordenes/${id}/preparar/`, datos).pipe(
        map((orden: OrdenDTO) => Orden.fromDTO(orden)),
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /**
   * Descarga un archivo PDF con las etiquetas de las órdenes.
   * @param {Parametros} filtros [Opcional] - Los filtros a aplicar.
   * @throws {ErrorFrontend} - Si hubo un error al descargar las etiquetas.
   * @author Juan Corral
   */
  public async descargarEtiquetas$(filtros?: Parametros): Promise<void> {
    const params: Parametros = filtros ?? {};
    await firstValueFrom(
      this._http
        .get('ordenes/descargar_etiquetas/', {
          params,
          responseType: 'blob',
          observe: 'response',
        })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            throw new ErrorFrontend(error);
          }),
          map((response) => {
            const blob = response.body as Blob;
            const disposition = response.headers.get('Content-Disposition');
            let filename = 'etiquetas.pdf';
            if (disposition) {
              const match = disposition.match(/filename="(.+)"/);
              if (match) filename = match[1];
            }
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            link.click();
            URL.revokeObjectURL(link.href);
            return;
          }),
        ),
    );
  }

  /**
   * Envía un archivo PDF con las etiquetas de las órdenes al correo del usuario.
   * @param {Parametros} filtros [Opcional] - Los filtros a aplicar.
   * @throws {ErrorFrontend} - Si hubo un error al enviar las etiquetas.
   * @author Juan Corral
   */
  public async enviarEtiquetas$(filtros?: Parametros): Promise<void> {
    const params: Parametros = filtros ?? {};
    await firstValueFrom(
      this._http.get('ordenes/enviar_etiquetas/', { params }).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /**
   * Recoge la orden y le asigna un domicilio.
   * @param {number} id - El id de la orden a recoger.
   * @throws {ErrorFrontend} - Si hubo un error al recoger la orden.
   * @autor Juan Corral
   */
  public async recogerOrden$(id: number): Promise<void> {
    await firstValueFrom(
      this._http.post<void>('ordenes/' + id + '/recoger/', {}).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /**
   * Descarga un archivo Excel con las órdenes.
   * @param {Parametros} filtros [Opcional] - Los filtros a aplicar.
   * @throws {ErrorFrontend} - Si hubo un error al descargar las órdenes.
   * @author Juan Corral
   */
  public async descargarOrdenes$(filtros?: Parametros): Promise<void> {
    const params: Parametros = filtros ?? {};
    await firstValueFrom(
      this._http
        .get('ordenes/descargar/', { params, responseType: 'blob' })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            throw new ErrorFrontend(error);
          }),
          map((blob: Blob) => {
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = 'ordenes.xlsx';
            link.click();
            URL.revokeObjectURL(link.href);
            return;
          }),
        ),
    );
  }

  /**
   * Obtiene información de la fórmula de una orden del servidor.
   * @param {number} id - El id de la fórmula a obtener.
   * @returns {Promise<FormulaDTO> } - La respuesta del servidor (promise).
   * @throws {ErrorFrontend} - Si hubo un error al obtener la orden.
   * @author Juan Corral
   */
  public obtenerFormula$(id: number): Promise<FormulaDTO> {
    return firstValueFrom(
      this._http.get<FormulaDTO>('ordenes/formulas/' + id).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /**
   * Obtiene información del item de una orden del servidor.
   * @param {number} id - El id del item a obtener.
   * @returns {Promise<ItemOrdenDTO> } - La respuesta del servidor (promise).
   * @throws {ErrorFrontend} - Si hubo un error al obtener la orden.
   * @author Juan Corral
   */
  public obtenerItem$(id: number): Promise<ItemOrdenDTO> {
    return firstValueFrom(
      this._http.get<ItemOrdenDTO>('ordenes/items/' + id).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }
}
